import React, { createContext, useContext, useEffect, useReducer } from 'react';
import { liveMatchService } from '../../api/service.ts';
import { LiveMatchState } from './liveMatchTypes.ts';
import { LiveMatchAction, liveMatchReducer } from './liveMatchReducer.ts';

// ─── Stub per lo stato iniziale prima di INIT ────────────────────────────────

const EMPTY_STATE: LiveMatchState = {
  eventId: 0,
  homeTeamName: '',
  awayTeamName: '',
  homeTeamId: 0,
  awayTeamId: 0,
  ourTeamSide: 'HOME',
  homeLiberoId: null,
  awayLiberoId: null,
  sessionId: null,
  strategy: null,
  servingTeam: null,
  homeShirtColor: '#1565C0',
  awayShirtColor: '#B71C1C',
  homeAllPlayers: [],
  awayAllPlayers: [],
  homeCallup: [],
  awayCallup: [],
  currentPhase: 'CALLUP_SELECTION',
  lineupSetupSide: 'HOME',
  currentSet: 1,
  currentSetScore: { homePoints: 0, awayPoints: 0 },
  setsWon: { home: 0, away: 0 },
  completedSets: [],
  homeLineup: [],
  awayLineup: [],
  homeBench: [],
  awayBench: [],
  homeTimeouts: 0,
  awayTimeouts: 0,
  servingTeamNow: null,
  eventLog: [],
  matchStartedAt: null,
  setStartedAt: null,
  eventDate: '',
};

// ─── Context shape ───────────────────────────────────────────────────────────

interface LiveMatchContextValue {
  state: LiveMatchState;
  dispatch: React.Dispatch<LiveMatchAction>;
}

const LiveMatchContext = createContext<LiveMatchContextValue | null>(null);

// ─── localStorage helpers ────────────────────────────────────────────────────

function storageKey(eventId: number): string {
  return `live-match-${eventId}`;
}

export function loadPersistedState(eventId: number): LiveMatchState | null {
  try {
    const raw = localStorage.getItem(storageKey(eventId));
    if (!raw) return null;
    return JSON.parse(raw) as LiveMatchState;
  } catch {
    return null;
  }
}

export function clearPersistedState(eventId: number): void {
  localStorage.removeItem(storageKey(eventId));
}

// ─── Provider ────────────────────────────────────────────────────────────────

interface Props {
  children: React.ReactNode;
}

export function LiveMatchProvider({ children }: Props) {
  const [state, dispatch] = useReducer(liveMatchReducer, EMPTY_STATE);

  // Persiste ogni cambiamento su localStorage e sincronizza il log sul BE
  useEffect(() => {
    if (state.eventId === 0) return;

    // Salvataggio locale (sempre)
    localStorage.setItem(storageKey(state.eventId), JSON.stringify(state));

    // Sincronizzazione log sul BE (solo se esiste una sessione)
    if (state.sessionId !== null) {
      const homeShirts: Record<number, number> = {};
      state.homeCallup.forEach((p) => { homeShirts[p.id] = p.shirtNumber; });
      const awayShirts: Record<number, number> = {};
      state.awayCallup.forEach((p) => { awayShirts[p.id] = p.shirtNumber; });
      const callupDataJson = JSON.stringify({
        homeShirts,
        awayShirts,
        homeLiberoId: state.homeLiberoId,
        awayLiberoId: state.awayLiberoId,
        ourTeamSide: state.ourTeamSide,
        homeTeamName: state.homeTeamName,
        awayTeamName: state.awayTeamName,
        homeShirtColor: state.homeShirtColor,
        awayShirtColor: state.awayShirtColor,
      });
      liveMatchService
        .updateLog(state.sessionId, JSON.stringify(state.eventLog), callupDataJson)
        .catch(() => { /* errori di rete silenziosi: localStorage è il source of truth */ });
    }

    // Se la partita è finita, ripulisci localStorage
    if (state.currentPhase === 'MATCH_END') {
      clearPersistedState(state.eventId);
    }
  }, [state]);

  return (
    <LiveMatchContext.Provider value={{ state, dispatch }}>
      {children}
    </LiveMatchContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useLiveMatch(): LiveMatchContextValue {
  const ctx = useContext(LiveMatchContext);
  if (!ctx) throw new Error('useLiveMatch must be used inside LiveMatchProvider');
  return ctx;
}

// ─── Utility: controlla se esiste una sessione attiva in localStorage ────────

export function hasActiveSession(eventId: number): boolean {
  const saved = loadPersistedState(eventId);
  return saved !== null && saved.currentPhase !== 'MATCH_END';
}
